import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Activity, 
  Users,
  Settings,
  BarChart3,
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Download,
  Eye,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';

const AdminPage = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState([]);
  const [weights, setWeights] = useState({
    waist_weight: 25,
    sleep_weight: 15,
    alcohol_weight: 15,
    activity_weight: 15,
    nutrition_weight: 10,
    stress_weight: 10,
    family_history_weight: 10
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    why_explanation: '',
    category: 'nutrition',
    target_drivers: [],
    difficulty: 'moderate',
    metric: '',
    success_target: ''
  });

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, rulesRes, weightsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/rules'),
        api.get('/admin/scoring-weights')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRules(rulesRes.data);
      setWeights(weightsRes.data);
    } catch (error) {
      console.error('Admin data error:', error);
      // Only show error if we haven't loaded any data
      if (!stats && !users.length) {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await api.get(`/admin/user/${userId}`);
      setSelectedUser(response.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleSaveWeights = async () => {
    try {
      await api.put('/admin/scoring-weights', weights);
      toast.success('Scoring weights updated');
    } catch (error) {
      toast.error('Failed to update weights');
    }
  };

  const handleCreateRule = async () => {
    try {
      await api.post('/admin/rules', newRule);
      toast.success('Rule created');
      setRuleDialogOpen(false);
      setNewRule({
        name: '',
        description: '',
        why_explanation: '',
        category: 'nutrition',
        target_drivers: [],
        difficulty: 'moderate',
        metric: '',
        success_target: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;
    try {
      await api.put(`/admin/rules/${editingRule.id}`, newRule);
      toast.success('Rule updated');
      setEditingRule(null);
      setRuleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('Delete this rule?')) return;
    try {
      await api.delete(`/admin/rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/admin/export-csv');
      const csv = [
        Object.keys(response.data[0]).join(','),
        ...response.data.map(row => Object.values(row).join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trajectory-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('CSV exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setNewRule({
      name: rule.name,
      description: rule.description,
      why_explanation: rule.why_explanation,
      category: rule.category,
      target_drivers: rule.target_drivers,
      difficulty: rule.difficulty,
      metric: rule.metric || '',
      success_target: rule.success_target || ''
    });
    setRuleDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center">
        <Activity className="w-12 h-12 text-[#2F3E30] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#525252] hover:text-[#1A1C19]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <FigIcon className="w-6 h-6 text-[#2F3E30]" />
              <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>ADMIN PANEL</span>
            </div>
          </div>
          <Button 
            onClick={handleExportCSV}
            variant="outline"
            className="border-[#D1D1CD]"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4">
              <p className="text-sm text-[#525252]">Total Users</p>
              <p className="text-3xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>{stats?.total_users || 0}</p>
            </div>
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4">
              <p className="text-sm text-[#525252]">Paid Users</p>
              <p className="text-3xl font-bold text-[#3F6212]" style={{ fontFamily: 'Libre Baskerville, serif' }}>{stats?.paid_users || 0}</p>
            </div>
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4">
              <p className="text-sm text-[#525252]">Conversion</p>
              <p className="text-3xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>{stats?.conversion_rate || 0}%</p>
            </div>
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4">
              <p className="text-sm text-[#525252]">Questionnaire %</p>
              <p className="text-3xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>{stats?.questionnaire_completion_rate || 0}%</p>
            </div>
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4">
              <p className="text-sm text-[#525252]">Total Check-ins</p>
              <p className="text-3xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>{stats?.total_checkins || 0}</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="users" className="space-y-6">
            <TabsList className="bg-[#EBEBE6] border border-[#D1D1CD]">
              <TabsTrigger value="users" className="data-[state=active]:bg-[#2F3E30] data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="rules" className="data-[state=active]:bg-[#2F3E30] data-[state=active]:text-white">
                <Settings className="w-4 h-4 mr-2" />
                Rules
              </TabsTrigger>
              <TabsTrigger value="scoring" className="data-[state=active]:bg-[#2F3E30] data-[state=active]:text-white">
                <BarChart3 className="w-4 h-4 mr-2" />
                Scoring
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="bg-[#EBEBE6] border border-[#D1D1CD] overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#D1D1CD] bg-[#E8E4DC]">
                      <TableHead className="text-[#1A1C19]">Email</TableHead>
                      <TableHead className="text-[#1A1C19]">Name</TableHead>
                      <TableHead className="text-[#1A1C19]">Paid</TableHead>
                      <TableHead className="text-[#1A1C19]">Questionnaire</TableHead>
                      <TableHead className="text-[#1A1C19]">Active Plan</TableHead>
                      <TableHead className="text-[#1A1C19]">Joined</TableHead>
                      <TableHead className="text-[#1A1C19]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="border-[#D1D1CD] bg-white hover:bg-[#E8E4DC]">
                        <TableCell className="text-[#1A1C19]">{u.email}</TableCell>
                        <TableCell className="text-[#1A1C19]">{u.name || '-'}</TableCell>
                        <TableCell>
                          {u.is_paid ? (
                            <CheckCircle className="w-5 h-5 text-[#3F6212]" />
                          ) : (
                            <XCircle className="w-5 h-5 text-[#525252]" />
                          )}
                        </TableCell>
                        <TableCell>
                          {u.questionnaire_completed ? (
                            <CheckCircle className="w-5 h-5 text-[#3F6212]" />
                          ) : (
                            <XCircle className="w-5 h-5 text-[#525252]" />
                          )}
                        </TableCell>
                        <TableCell>
                          {u.has_active_plan ? (
                            <CheckCircle className="w-5 h-5 text-[#3F6212]" />
                          ) : (
                            <XCircle className="w-5 h-5 text-[#525252]" />
                          )}
                        </TableCell>
                        <TableCell className="text-[#1A1C19]">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewUser(u.id)}
                            className="text-[#1A1C19]"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* User Detail Dialog */}
              {selectedUser && (
                <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
                  <DialogContent className="bg-[#EBEBE6] border-[#D1D1CD] max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>User Details</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-bold mb-2">Profile</h4>
                        <pre className="bg-[#E8E4DC] p-4 rounded text-xs overflow-auto">
                          {JSON.stringify(selectedUser.user, null, 2)}
                        </pre>
                      </div>
                      {selectedUser.score && (
                        <div>
                          <h4 className="font-bold mb-2">Latest Score</h4>
                          <pre className="bg-[#E8E4DC] p-4 rounded text-xs overflow-auto">
                            {JSON.stringify(selectedUser.score, null, 2)}
                          </pre>
                        </div>
                      )}
                      {selectedUser.plan && (
                        <div>
                          <h4 className="font-bold mb-2">Current Plan</h4>
                          <pre className="bg-[#E8E4DC] p-4 rounded text-xs overflow-auto">
                            {JSON.stringify(selectedUser.plan, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </TabsContent>

            {/* Rules Tab */}
            <TabsContent value="rules">
              <div className="flex justify-between mb-4">
                <h3 className="font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>RULES CATALOG</h3>
                <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-[#2F3E30] text-white"
                      onClick={() => {
                        setEditingRule(null);
                        setNewRule({
                          name: '',
                          description: '',
                          why_explanation: '',
                          category: 'nutrition',
                          target_drivers: [],
                          difficulty: 'moderate'
                        });
                      }}
                      data-testid="add-rule-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Rule
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#EBEBE6] border-[#D1D1CD]">
                    <DialogHeader>
                      <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={newRule.name}
                          onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                          className="mt-1 bg-[#E8E4DC] border-[#D1D1CD]"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={newRule.description}
                          onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                          className="mt-1 bg-[#E8E4DC] border-[#D1D1CD]"
                        />
                      </div>
                      <div>
                        <Label>Why Explanation</Label>
                        <Textarea
                          value={newRule.why_explanation}
                          onChange={(e) => setNewRule(prev => ({ ...prev, why_explanation: e.target.value }))}
                          className="mt-1 bg-[#E8E4DC] border-[#D1D1CD]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Category</Label>
                          <Select
                            value={newRule.category}
                            onValueChange={(value) => setNewRule(prev => ({ ...prev, category: value }))}
                          >
                            <SelectTrigger className="mt-1 bg-[#E8E4DC] border-[#D1D1CD]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nutrition">Nutrition</SelectItem>
                              <SelectItem value="sleep">Sleep</SelectItem>
                              <SelectItem value="alcohol">Alcohol</SelectItem>
                              <SelectItem value="movement">Movement</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Difficulty</Label>
                          <Select
                            value={newRule.difficulty}
                            onValueChange={(value) => setNewRule(prev => ({ ...prev, difficulty: value }))}
                          >
                            <SelectTrigger className="mt-1 bg-[#E8E4DC] border-[#D1D1CD]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="easy">Easy</SelectItem>
                              <SelectItem value="moderate">Moderate</SelectItem>
                              <SelectItem value="hard">Hard</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Metric (What to track)</Label>
                          <Input
                            value={newRule.metric}
                            onChange={(e) => setNewRule(prev => ({ ...prev, metric: e.target.value }))}
                            placeholder="e.g., Days per week"
                            className="mt-1 bg-[#E8E4DC] border-[#D1D1CD]"
                          />
                        </div>
                        <div>
                          <Label>Success Target</Label>
                          <Input
                            value={newRule.success_target}
                            onChange={(e) => setNewRule(prev => ({ ...prev, success_target: e.target.value }))}
                            placeholder="e.g., 5+ days per week"
                            className="mt-1 bg-[#E8E4DC] border-[#D1D1CD]"
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button 
                        onClick={editingRule ? handleUpdateRule : handleCreateRule}
                        className="bg-[#2F3E30] text-white"
                      >
                        {editingRule ? 'Update' : 'Create'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="bg-[#EBEBE6] border border-[#D1D1CD] overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#D1D1CD]">
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Difficulty</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((rule) => (
                      <TableRow key={rule.id} className="border-[#D1D1CD]">
                        <TableCell className="font-medium">{rule.name}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded bg-[#D1D1CD] text-xs">
                            {rule.category}
                          </span>
                        </TableCell>
                        <TableCell>{rule.difficulty}</TableCell>
                        <TableCell className="max-w-xs truncate">{rule.description}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditRule(rule)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRule(rule.id)}
                              className="text-[#EF4444]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Scoring Tab */}
            <TabsContent value="scoring">
              <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
                <h3 className="font-bold mb-6" style={{ fontFamily: 'Libre Baskerville, serif' }}>SCORING WEIGHTS</h3>
                <p className="text-sm text-[#525252] mb-6">
                  Adjust the weight of each factor in the overall score calculation. Values should sum to 100.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(weights).map(([key, value]) => {
                    const labels = {
                      waist_weight: 'Waist/Visceral',
                      sleep_weight: 'Sleep',
                      alcohol_weight: 'Alcohol',
                      activity_weight: 'Activity',
                      nutrition_weight: 'Nutrition',
                      stress_weight: 'Stress',
                      family_history_weight: 'Family History'
                    };
                    return (
                      <div key={key}>
                        <Label className="text-sm">{labels[key]} Weight</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={value}
                            onChange={(e) => setWeights(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                            className="bg-[#E8E4DC] border-[#D1D1CD]"
                          />
                          <span className="flex items-center text-[#525252]">%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 p-4 rounded-lg bg-[#E8E4DC]">
                  <p className="text-sm">
                    Current total: <strong className={Object.values(weights).reduce((a, b) => a + b, 0) === 100 ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                      {Object.values(weights).reduce((a, b) => a + b, 0)}%
                    </strong>
                    {Object.values(weights).reduce((a, b) => a + b, 0) !== 100 && (
                      <span className="text-[#F59E0B] ml-2">(should equal 100%)</span>
                    )}
                  </p>
                </div>
                <Button 
                  onClick={handleSaveWeights}
                  className="mt-6 bg-[#2F3E30]"
                  data-testid="save-weights-btn"
                >
                  Save Weights
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
